import { Employee } from '../types/Employee/Employee'
import { Constraint } from '../types/Work/Constraint'
import {
	PaginatedWorkItems,
	TakeOffComponentWorkItem,
	TakeOffConsumableWorkItem,
	WorkItem,
	WorkItemComponentValue,
	TaskConsumableValue,
} from '../types/Work/WorkItem'
import {
	getAll,
	getOne,
	post,
	put,
	deleteOne,
	patch,
	postWithResponse,
	deleteMany,
	processWithConcurrency,
} from './CommonServices/httpService'
import { ImportResponse } from '../types/Upload/ImportResponse'
import { BlockBlobClient, BlockBlobParallelUploadOptions } from '@azure/storage-blob'
import { ConstraintAttachment } from '../types/Work/ConstraintAttachment'
import { ProgressAnalysis } from '../types/Work/ProgressRecord'
import { WorkItemStatus } from '../types/Work/WorkItemStatus'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import {
	addConstraintFB,
	getConstraintsByWorkItemIdFB,
	updateWorkItemProgressFB,
	clearConstraintFB,
} from './CacheFallbackServices/workItemsFallback'
import { hasConnection } from './CommonServices/healthGate'
import { showErrorToast } from './CommonServices/alertServices'

const getEmployeeWorkItems = async (
	statusFilter: WorkItemStatus,
	id?: number,
	page?: number,
	searchText?: string,
): Promise<{ employee: Employee; directReports: Employee[]; allWorkItemsCount: number }> => {
	const result = await getAll<{
		employee: Employee
		directReports: Employee[]
		allWorkItemsCount: number
	}>(
		`/Workers/${id}/WorkItems/Assign/Roots?
		${page ? `page=${page}` : ''}
		${searchText ? `&searchText=${encodeURIComponent(searchText)}` : ''}
		&status=${statusFilter}
		`,
		'Failed to get employee work items.',
	)

	return {
		employee: result?.data?.employee,
		directReports: result?.data?.directReports ?? [],
		allWorkItemsCount: result?.data?.allWorkItemsCount ?? 0,
	}
}

const getEmployeeWorkItemChildren = async (
	statusFilter: WorkItemStatus,
	id: number,
	workItemId: number,
	searchText?: string,
): Promise<WorkItem[]> => {
	const result = await getAll<WorkItem[]>(
		`/Workers/${id}/WorkItems/Assign/${workItemId}/Children?status=${encodeURIComponent(
			statusFilter,
		)}${searchText ? `&searchText=${encodeURIComponent(searchText)}` : ''}`,
		'Failed to get work item children.',
	)
	return result.data ?? []
}
const assignWorkItems = async (
	workItemIds: number[],
	toId: number,
	fromId: number,
	assignAction: boolean,
): Promise<boolean> => {
	const result = await post(`/Workers/${toId}/${fromId}/WorkItems?assignAction=${assignAction}`, workItemIds)
	return result.success
}

const unassignWorkItem = async (
	workItemIds: number[],
	fromEmployeeId: number,
): Promise<boolean> => {
	const result = await deleteMany(`/Workers/${fromEmployeeId}/WorkItems`, workItemIds)
	return result.success
}

type AddConstraintRequest = {
	comment: string
	attachmentCount: number
}
const addConstraint = async (
	workItemId: number,
	workerId: number,
	constraintComment: string,
	constraintAttachments: Blob[] | null,
	attachmentMimeType?: string,
	db?: ForemanDB | null,
): Promise<boolean> => {
	try {
		if (!(await hasConnection()) && db) {
			try {
				await addConstraintFB(
					db,
					workItemId,
					workerId,
					constraintComment,
					constraintAttachments?.[0] ?? null,
				)
				return true
			} catch (error: any) {
				showErrorToast({ message: error.message || 'Failed to add constraint in offline mode' })
				throw error
			}
		}
		// no constraint attachemnt
		const rq: AddConstraintRequest = {
			comment: constraintComment,
			attachmentCount: constraintAttachments?.length ?? 0,
		}

		const addConstraintResponse = await postWithResponse<ImportResponse[]>(
			`/WorkItems/${workItemId}/Constraints`,
			rq,
		)
		if (addConstraintResponse.data && constraintAttachments && constraintAttachments.length > 0) {
			const uploadTasks = constraintAttachments.map((file, index) => {
				// Create a BlobClient for the current file
				const blockBlobClient = new BlockBlobClient(
					`${addConstraintResponse.data[index].uploadUri}${addConstraintResponse.data[index].sasToken}`,
				)

				const options: BlockBlobParallelUploadOptions = {
					blobHTTPHeaders: { blobContentType: file.type },
				}

				// Upload file
				return () => blockBlobClient.uploadData(file, options)
			})
			await processWithConcurrency(uploadTasks)
		}
		return true
	} catch (error) {
		throw {
			message: 'Could not add constraint comment.',
		}
	}
}

const getConstraintsByWorkItemId = async (workItemId: number, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			return await getConstraintsByWorkItemIdFB(db, workItemId)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get constraints from cache' })
			throw error
		}
	}
	const result = await getAll<Constraint[]>(
		`/Constraints?workItemId=${workItemId}`,
		'Failed to get all Constraints',
	)
	return result.data ?? []
}

const updateWorkItemProgress = async (
	workItemId: number,
	taskId: number,
	progress: number,
	localDate?: Date,
	foremanId?: number,
	isOverride = false,
	db?: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db && foremanId) {
		try {
			await updateWorkItemProgressFB(db, workItemId, taskId, progress, foremanId)
			return true
		} catch (error: any) {
			showErrorToast({
				message: error.message || 'Failed to update work item progress in offline mode',
			})
			throw error
		}
	}
	const result = isOverride
		? await put(`/WorkItems/Task/${taskId}`, { progress })
		: await patch(
				`/WorkItems/Task/${taskId}/Foreman/${foremanId}?localDate=${encodeURIComponent(
					localDate?.toLocaleString() ?? '',
				)}`,
				{ progress },
		  )

	return result.success
}

const updateWorkItemData = (
	workItemId: number,
	newValue: number,
	field: 'QuantityBudgeted' | 'BudgetedHours',
) => {
	const url = `/WorkItems/${workItemId}/${field}`
	return patch(url, { [field]: newValue })
}

const getSecondaryWorkItems = async (workItemId: number): Promise<WorkItem> => {
	const result = await getAll<WorkItem>(
		`/WorkItems/${workItemId}`,
		'Failed to get Secondary Work Items',
	)
	return result.data ?? null
}

const getWorkItemChildren = async (workItemId: number): Promise<WorkItem> => {
	const result = await getAll<WorkItem>(
		`/WorkItems/${workItemId}/Children`,
		'Failed to get work item children',
	)
	return result.data
}

const getWorkItemPath = async (taskId?: number, reworkId?: number): Promise<number[]> => {
	const result = await getAll<number[]>(
		`/WorkItems/Path?${reworkId ? `reworkId=${reworkId}` : `taskId=${taskId}`}`,
		'Failed to get work item path',
	)
	return result.data
}

const getFirstLayerWorkItems = async (
	searchText: string,
	page: number,
	completedOnly = false,
	workItemId?: number,
): Promise<PaginatedWorkItems> => {
	const result = await getAll<PaginatedWorkItems>(
		`/WorkItems?
		${searchText === '' ? '' : `searchText=${encodeURIComponent(searchText)}&`}
		page=${page}
		&completed=${completedOnly}
		${workItemId ? `&workItemId=${workItemId}` : ''}`,
		'Failed to get Search results',
	)
	return result.data ?? null
}

const getAllConstraints = async (): Promise<Constraint[]> => {
	const result = await getAll<Constraint[]>('/Constraints', 'Failed to get all Constraints')
	return result.data ?? []
}
const getForemanConstraints = async (foremanId: number): Promise<Constraint[]> => {
	const result = await getAll<Constraint[]>(
		`/Constraints/Foreman/${foremanId}`,
		'Failed to get foreman constraints',
	)
	return result.data ?? []
}

const getConstraintAttachment = async (constraint: Constraint): Promise<ConstraintAttachment[]> => {
	const result = await getAll<ConstraintAttachment[]>(
		`/Constraints/${constraint.id}/Attachments`,
		'Failed to get Constraint Attachments',
	)
	return result.data ?? []
}

const clearConstraint = async (constraint: Constraint, db?: ForemanDB | null): Promise<boolean> => {
	if (!(await hasConnection()) && db) {
		try {
			await clearConstraintFB(db, constraint.id)
			return true
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to clear constraint in offline mode' })
			throw error
		}
	}
	const result = await deleteOne(`/Constraints/${constraint.id}`)
	return result.success
}

const clearAllConstraintsOfWorkItem = async (workItemId: number): Promise<boolean> => {
	const result = await deleteOne(`/Constraints/?workItemId=${workItemId}`)
	return result.success
}

const deleteWorkItem = async (workItemId: number): Promise<boolean> => {
	const result = await deleteOne(`/WorkItems/${workItemId}`)
	return result.success
}

const updateWorkItem = async (workItem: WorkItem): Promise<boolean> => {
	const result = await put('/WorkItems', {
		id: workItem.id,
		budgetedHours: workItem.budgetedHours,
		releaseDate: workItem.releaseDate,
		nocDate: workItem.nocDate,
		quantity: workItem.quantityBudgeted,
	})
	return result.success
}

const getDirectReportWorkItem = async (directReportdId: number): Promise<Employee> => {
	const result = await getOne<Employee>(`/Workers/${directReportdId}/WorkItems/DirectReport/Roots`)
	return result.data
}

const getDirectReportWorkItemChildren = async (
	directReportId: number,
	workItemId: number,
): Promise<WorkItem[]> => {
	const result = await getAll<WorkItem[]>(
		`/Workers/${directReportId}/WorkItems/DirectReport/${workItemId}/Children`,
		'Failed to get work item children.',
	)
	return result.data ?? []
}

const getDirectReportSearchMatches = async (
	directReportId: number,
	searchText: string,
): Promise<number[]> => {
	const result = await getAll<number[]>(
		`/Workers/${directReportId}/WorkItems/DirectReport/SearchMatches?searchText=${encodeURIComponent(
			searchText,
		)}`,
	)
	return result.data ?? []
}

const getWorkItemProgress = async (workItemId: number): Promise<ProgressAnalysis> => {
	const result = await getAll<ProgressAnalysis>(`/WorkItems/${workItemId}/Progress`)
	return result.data
}

const getComponentTemplate = async (
	craftId: number,
	wbsId: number,
): Promise<TakeOffComponentWorkItem[]> => {
	const result = await getAll<TakeOffComponentWorkItem[]>(
		`/WorkItems/TakeOff/Components?craftId=${craftId}&wbsId=${wbsId}`,
	)
	return result.data
}

const getConsumableTemplate = async (specId: number): Promise<TakeOffConsumableWorkItem[]> => {
	const result = await getAll<TakeOffConsumableWorkItem[]>(
		`/WorkItems/TakeOff/Consumables?specId=${specId}`,
	)
	return result.data
}

const getWorkItemComponentValues = async (
	workItemId: number,
): Promise<WorkItemComponentValue[]> => {
	const result = await getAll<WorkItemComponentValue[]>(`/WorkItems/${workItemId}/Components`)
	return result.data
}

const getTaskConsumableValues = async (taskId: number): Promise<TaskConsumableValue[]> => {
	const result = await getAll<TaskConsumableValue[]>(`/WorkItems/Task/${taskId}/Consumables`)
	return result.data
}

const updateWorkItemComponentValues = async (
	workItemId: number,
	componentValues: WorkItemComponentValue[],
): Promise<boolean> => {
	const result = await patch(`/WorkItems/${workItemId}/Components`, componentValues)
	return result.success
}

const updateWorkItemConsumableValues = async (
	consumableValues: TaskConsumableValue[],
): Promise<boolean> => {
	const result = await patch(`/WorkItems/Consumables`, consumableValues)
	return result.success
}

export {
	getEmployeeWorkItems,
	getEmployeeWorkItemChildren,
	getDirectReportWorkItemChildren,
	getDirectReportSearchMatches,
	assignWorkItems,
	unassignWorkItem,
	updateWorkItemProgress,
	getFirstLayerWorkItems as getAllWorkItems,
	updateWorkItemData,
	getAllConstraints,
	clearConstraint,
	addConstraint,
	getConstraintAttachment,
	deleteWorkItem,
	updateWorkItem,
	getConstraintsByWorkItemId,
	clearAllConstraintsOfWorkItem,
	getSecondaryWorkItems,
	getWorkItemChildren,
	getWorkItemPath,
	getDirectReportWorkItem,
	getWorkItemProgress,
	getComponentTemplate,
	getConsumableTemplate,
	getWorkItemComponentValues,
	getTaskConsumableValues,
	updateWorkItemComponentValues,
	updateWorkItemConsumableValues,
	getForemanConstraints,
}
